import { useState } from 'react';
import type { Ref } from 'react';
import { Tooltip } from '@odo/shared-ui';
import { useIsTextTruncated } from '../../../../hooks/useIsTextTruncated';
import type { StepMeta } from './clinicalSteps';

// Fits "Manufacturer" whole but truncates "Notes / Instructions", keeping the strip short.
const TAB_LABEL_MAX_WIDTH_CLASS = 'max-w-24';

interface WizardStepTabProps {
    step: StepMeta;
    index: number;
    isActive: boolean;
    isPast: boolean;
    jumpBlocked: boolean;
    jumpBlockedHint?: string;
    hasConnector: boolean;
    onSelect: (index: number) => void;
    tabRef?: Ref<HTMLButtonElement>;
}

function tabClassName(jumpBlocked: boolean, isActive: boolean, isPast: boolean): string {
    if (jumpBlocked) {
        return 'bg-gray-50 text-gray-300 cursor-not-allowed';
    }
    if (isActive) {
        return 'bg-gray-900 text-white cursor-pointer';
    }
    return isPast
        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer'
        : 'bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600 cursor-pointer';
}

export function WizardStepTab({
    step,
    index,
    isActive,
    isPast,
    jumpBlocked,
    jumpBlockedHint,
    hasConnector,
    onSelect,
    tabRef
}: WizardStepTabProps) {
    const Icon = step.icon;
    const [labelElement, setLabelElement] = useState<HTMLSpanElement | null>(null);
    const isLabelTruncated = useIsTextTruncated(labelElement, step.label);

    return (
        <div className="flex items-center">
            <Tooltip content={step.label} disabled={jumpBlocked || !isLabelTruncated}>
                <button
                    ref={tabRef}
                    type="button"
                    onClick={() => {
                        if (!jumpBlocked) onSelect(index);
                    }}
                    disabled={jumpBlocked}
                    aria-current={isActive ? 'step' : undefined}
                    title={jumpBlocked ? jumpBlockedHint : undefined}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 ${tabClassName(jumpBlocked, isActive, isPast)}`}
                >
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span ref={setLabelElement} className={`truncate ${TAB_LABEL_MAX_WIDTH_CLASS}`}>
                        {step.label}
                    </span>
                </button>
            </Tooltip>
            {hasConnector && (
                <div className={`w-6 h-px mx-1 ${isPast ? 'bg-gray-300' : 'bg-gray-200'}`} />
            )}
        </div>
    );
}
